import type { IProjectRole, ICreateProjectRoleDto, IUpdateProjectRoleDto } from 'src/types/project-role';

import useSWR from 'swr';
import { useMemo } from 'react';

import axiosInstance, { endpoints, fetcher } from 'src/utils/axios';

interface PageDto<T> {
  data: T[];
  meta: {
    page: number;
    take: number;
    itemCount: number;
    pageCount: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}

export interface InspectorEligibleRoles {
  inspectorId: number;
  eligibleProjectRoleIds: number[];
}

export interface EligibleProjectRolesResponse {
  inspectors: InspectorEligibleRoles[];
}

export interface PreAssignedProjectRole {
  inspectorId: number;
  projectRoleId: number;
}

export interface InspectorSuggestedRole {
  inspectorId: number;
  suggestedProjectRoleId: number | null;
}

export interface SuggestProjectRolesResponse {
  inspectors: InspectorSuggestedRole[];
}

export function useGetProjectRoles() {
  // Fetch all project roles - using max allowed limit of 300
  const url = `${endpoints.projectRoles.list}?page=1&take=300`;

  const { data, isLoading, error, isValidating, mutate } = useSWR<PageDto<IProjectRole>>(url, fetcher);

  const memoizedValue = useMemo(
    () => ({
      projectRoles: data?.data || [],
      projectRolesLoading: isLoading,
      projectRolesError: error,
      projectRolesValidating: isValidating,
      projectRolesEmpty: !isLoading && !data?.data?.length,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export async function createProjectRole(data: ICreateProjectRoleDto): Promise<IProjectRole> {
  const res = await axiosInstance.post(endpoints.projectRoles.create, data);
  return res.data;
}

export async function updateProjectRole(
  id: number,
  data: IUpdateProjectRoleDto
): Promise<IProjectRole> {
  const res = await axiosInstance.put(endpoints.projectRoles.update(id), data);
  return res.data;
}

export async function deleteProjectRole(id: number): Promise<void> {
  await axiosInstance.delete(endpoints.projectRoles.delete(id));
}

export async function getProjectRole(id: number): Promise<IProjectRole> {
  const res = await axiosInstance.get(endpoints.projectRoles.details(id));
  return res.data;
}

export async function getEligibleProjectRolesForInspectors(
  inspectorIds: number[],
  activityId: number
): Promise<EligibleProjectRolesResponse> {
  const res = await axiosInstance.post(endpoints.projectRoles.eligibleForInspectors, {
    inspectorIds,
    activityId,
  });
  return res.data;
}

export async function suggestProjectRolesForInspectors(
  inspectorIds: number[],
  activityId: number,
  preAssignedRoles?: PreAssignedProjectRole[]
): Promise<SuggestProjectRolesResponse> {
  const res = await axiosInstance.post(endpoints.projectRoles.suggestForInspectors, {
    inspectorIds,
    activityId,
    ...(preAssignedRoles && preAssignedRoles.length > 0 && { preAssignedRoles }),
  });
  return res.data;
}
